import { FC, useState } from 'react';
import { Box, Dialog } from '@material-ui/core';
import { SButton, SText } from '@setvi/shared/components';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import moment, { Moment } from 'moment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { renewQuoteV2Mutation } from 'Services/Mutations/QuotesV2';
import { QuotesV2QueryKey } from 'Services/Query/QuotesV2';
import type { PriceRefreshMethod } from 'Services/Query/QuotesV2/Types';
import { ExpiredBadge } from '../expired-badge';
import { ExtendDateDialog } from '../expired-quote-dialog';
import { useQuoteCache } from '../../hooks/use-quote-cache';

interface ExpiredWarningProps {
  quoteId?: string;
  validUntil: number;
  customDate?: boolean;
  onSuccess: (quote: any) => void;
}

export const ExpiredWarning: FC<ExpiredWarningProps> = ({
  quoteId,
  validUntil,
  customDate,
  onSuccess
}) => {
  const [showDateDialog, setShowDateDialog] = useState(false);
  const queryClient = useQueryClient();
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = renewQuoteV2Mutation();

  const { mutateAsync: renewQuote, isLoading: isRenewingQuote } = useMutation({
    mutationFn: (params: {
      validUntil: string;
      refreshMethod: PriceRefreshMethod;
    }) => mutationFn({ quoteId, ...params }),
    onSuccess: async () => {
      await cache.refetchQuote();
      queryClient.invalidateQueries({
        queryKey: [QuotesV2QueryKey.GET_QUOTES]
      });
      const freshQuote = cache.getQuote();
      onSuccess?.(freshQuote);
    }
  });

  const title = customDate
    ? "This quote is no longer valid. To continue, you'll need to extend the expiration date."
    : 'Quote expiration date has passed. Please see the updated date below based on company quoting policy.';

  const handleChooseDate = () => {
    setShowDateDialog(true);
  };

  const handleExtend = async () => {
    await renewQuote({
      validUntil: moment()
        .add(validUntil + 1, 'days')
        .format('YYYY-MM-DD'),
      refreshMethod: 'KEEP_PRICING'
    });
  };

  const handleConfirmDate = async (date: Moment) => {
    await renewQuote({
      validUntil: date.format('YYYY-MM-DD'),
      refreshMethod: 'KEEP_PRICING'
    });
    setShowDateDialog(false);
  };

  return (
    <>
      <Box
        style={{
          padding: '12px 24px'
        }}>
        <Box
          style={{
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 4px 0 rgba(48, 54, 60, 0.16)',
            padding: '12px 16px',
            gap: 8,
            background: '#FED2D2'
          }}>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
            <ExpiredBadge />
            <SText fontColor="#333D47" size="sm" maxWidth={800} title={title} />
          </Box>

          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
            {customDate && (
              <SButton
                variant="outlined"
                color="default"
                disabled={isRenewingQuote}
                onClick={handleChooseDate}
                endIcon={
                  <KeyboardArrowDownRoundedIcon
                    style={{
                      marginBottom: 2
                    }}
                  />
                }
                style={{
                  border: '1px solid #AFBAC6',
                  minHeight: 28,
                  color: '#5D7083',
                  fontWeight: 600
                }}>
                Choose Date
              </SButton>
            )}
            <SButton
              disabled={isRenewingQuote}
              loading={isRenewingQuote}
              onClick={handleExtend}
              style={{
                background: !isRenewingQuote ? '#000' : '#00000042',
                minHeight: 28
              }}>
              Extend for {validUntil} Days
            </SButton>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={showDateDialog}
        onClose={() => setShowDateDialog(false)}
        PaperProps={{ style: { borderRadius: 20 } }}>
        <ExtendDateDialog
          defaultDays={validUntil}
          isLoading={isRenewingQuote}
          onConfirm={handleConfirmDate}
          onClose={() => setShowDateDialog(false)}
        />
      </Dialog>
    </>
  );
};
